import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import * as maptalks from "maptalks";
import "maptalks/dist/maptalks.css";
import { useAuth } from "../context/AuthContext";
import { OPENSTREETMAP_TILE_PROXY_PATH, appendMapTileAccessToken } from "../map/tileLayers";

export type AnnotationAreaFilterBBox = [number, number, number, number];

type StoredAreaFilterMapView = {
  center: [number, number];
  zoom: number;
};

type AnnotationAreaFilterDialogProps = {
  open: boolean;
  initialBBox: AnnotationAreaFilterBBox | null;
  onClose: () => void;
  onSelect: (bbox: AnnotationAreaFilterBBox) => void;
};

const DRAW_MODE = "freeHandRectangle";
const DEFAULT_CENTER: [number, number] = [20, 0];
const DEFAULT_ZOOM = 2;
const ANNOTATION_AREA_FILTER_MAP_VIEW_STORAGE_KEY = "sky-view.annotations.areaFilter.mapView";

const bboxToPolygonCoords = (bbox: AnnotationAreaFilterBBox): [number, number][] => {
  const [west, south, east, north] = bbox;
  return [
    [west, north],
    [east, north],
    [east, south],
    [west, south],
    [west, north]
  ];
};

const geometryToBBox = (geometry: maptalks.Geometry | null): AnnotationAreaFilterBBox | null => {
  if (!geometry) {
    return null;
  }

  const extent = geometry.getExtent();
  if (!extent || extent.xmin == null || extent.ymin == null || extent.xmax == null || extent.ymax == null) {
    return null;
  }

  return [extent.xmin, extent.ymin, extent.xmax, extent.ymax];
};

const createBBoxPolygon = (bbox: AnnotationAreaFilterBBox) =>
  new maptalks.Polygon(bboxToPolygonCoords(bbox), {
    symbol: {
      lineColor: "#1e88e5",
      lineWidth: 2,
      polygonFill: "rgba(30,136,229,0.15)",
      polygonOpacity: 0.4
    }
  });

const fitMapToBBox = (map: maptalks.Map, bbox: AnnotationAreaFilterBBox) => {
  const polygon = createBBoxPolygon(bbox);
  const extent = polygon.getExtent();
  if (extent) {
    map.fitExtent(extent, 40);
  }
};

const readStoredAreaFilterMapView = (): StoredAreaFilterMapView | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(ANNOTATION_AREA_FILTER_MAP_VIEW_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredAreaFilterMapView> | null;
    if (
      !parsed ||
      !Array.isArray(parsed.center) ||
      parsed.center.length !== 2 ||
      parsed.center.some((entry) => typeof entry !== "number" || !Number.isFinite(entry)) ||
      typeof parsed.zoom !== "number" ||
      !Number.isFinite(parsed.zoom)
    ) {
      return null;
    }

    return {
      center: [parsed.center[0], parsed.center[1]],
      zoom: parsed.zoom
    };
  } catch {
    return null;
  }
};

const persistAreaFilterMapView = (map: maptalks.Map | null) => {
  if (typeof window === "undefined" || !map) {
    return;
  }

  try {
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (!center || !Number.isFinite(center.x) || !Number.isFinite(center.y) || !Number.isFinite(zoom)) {
      return;
    }

    window.localStorage.setItem(
      ANNOTATION_AREA_FILTER_MAP_VIEW_STORAGE_KEY,
      JSON.stringify({ center: [center.x, center.y], zoom })
    );
  } catch {
    // ignore storage failures
  }
};

const AnnotationAreaFilterDialog = ({
  open,
  initialBBox,
  onClose,
  onSelect
}: AnnotationAreaFilterDialogProps) => {
  const { resolveApiPath, tokens } = useAuth();
  const openStreetMapTileUrl = useMemo(
    () => appendMapTileAccessToken(resolveApiPath(OPENSTREETMAP_TILE_PROXY_PATH), tokens?.accessToken),
    [resolveApiPath, tokens?.accessToken]
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maptalks.Map | null>(null);
  const selectionLayerRef = useRef<maptalks.VectorLayer | null>(null);
  const drawToolRef = useRef<maptalks.DrawTool | null>(null);
  const selectedGeometryRef = useRef<maptalks.Geometry | null>(null);
  const mapDraggableRef = useRef<boolean | null>(null);
  const [drawActive, setDrawActive] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const hasRestoredInitialViewRef = useRef(false);

  const syncMapLayout = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return () => {};
    }

    let disposed = false;

    const applyLayout = () => {
      if (disposed) {
        return;
      }

      map.checkSize();
    };

    const timeoutId = window.setTimeout(applyLayout, 200);
    requestAnimationFrame(() => {
      applyLayout();
      requestAnimationFrame(applyLayout);
    });

    return () => {
      disposed = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  const clearSelection = useCallback(() => {
    selectedGeometryRef.current?.remove();
    selectedGeometryRef.current = null;
    selectionLayerRef.current?.clear();
  }, []);

  const restoreMapDragging = useCallback(() => {
    const map = mapRef.current;
    if (map && mapDraggableRef.current !== null) {
      map.config("draggable", mapDraggableRef.current);
    }
    mapDraggableRef.current = null;
  }, []);

  const disableMapDragging = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (mapDraggableRef.current === null) {
      mapDraggableRef.current = Boolean(map.options?.draggable);
    }
    map.config("draggable", false);
  }, []);

  const attachGeometry = useCallback((geometry: maptalks.Geometry) => {
    clearSelection();
    geometry.setSymbol({
      lineColor: "#1e88e5",
      lineWidth: 2,
      polygonFill: "rgba(30,136,229,0.15)",
      polygonOpacity: 0.4
    });
    selectionLayerRef.current?.addGeometry(geometry);
    selectedGeometryRef.current = geometry;
  }, [clearSelection]);

  const handleDrawRectangle = useCallback(() => {
    const drawTool = drawToolRef.current;
    if (!drawTool || drawActive) {
      return;
    }

    clearSelection();
    disableMapDragging();
    setDrawActive(true);
    drawTool.setMode(DRAW_MODE);
    drawTool.enable();
  }, [clearSelection, disableMapDragging, drawActive]);

  useEffect(() => {
    if (!open || !containerRef.current || mapRef.current) {
      return;
    }

    const storedMapView = readStoredAreaFilterMapView();
    const initialCenter = storedMapView?.center ?? DEFAULT_CENTER;
    const initialZoom = storedMapView?.zoom ?? DEFAULT_ZOOM;

    const map = new maptalks.Map(containerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      centerCross: false,
      baseLayer: new maptalks.TileLayer("base", {
        urlTemplate: openStreetMapTileUrl
      }),
      zoomControl: true
    });
    mapRef.current = map;

    const selectionLayer = new maptalks.VectorLayer("annotation-area-filter-selection").addTo(map);
    selectionLayerRef.current = selectionLayer;

    const drawTool = new maptalks.DrawTool({
      mode: DRAW_MODE,
      once: true,
      symbol: {
        lineColor: "#1e88e5",
        lineWidth: 2,
        polygonFill: "rgba(30,136,229,0.15)",
        polygonOpacity: 0.4
      }
    });
    drawTool.addTo(map);
    drawTool.disable();
    drawToolRef.current = drawTool;

    const handleDrawStart = () => {
      setDrawActive(true);
    };

    const handleDrawEnd = (event: { geometry?: maptalks.Geometry }) => {
      setDrawActive(false);
      drawTool.disable();
      restoreMapDragging();

      if (!event.geometry) {
        return;
      }

      attachGeometry(event.geometry);
      const nextBBox = geometryToBBox(event.geometry);
      if (nextBBox) {
        onSelect(nextBBox);
      }
    };

    const handleDrawCancel = () => {
      setDrawActive(false);
      drawTool.disable();
      restoreMapDragging();
    };

    drawTool.on("drawstart", handleDrawStart);
    drawTool.on("drawend", handleDrawEnd);
    drawTool.on("drawcancel", handleDrawCancel);

    const handleMoveEnd = () => {
      persistAreaFilterMapView(map);
    };

    const handleZoomEnd = () => {
      persistAreaFilterMapView(map);
    };

    map.on("moveend", handleMoveEnd);
    map.on("zoomend", handleZoomEnd);

    const cleanupLayoutSync = syncMapLayout();
    if (initialBBox) {
      const geometry = createBBoxPolygon(initialBBox);
      attachGeometry(geometry);
    }

    setMapError(null);

    return () => {
      cleanupLayoutSync();
      persistAreaFilterMapView(map);
      drawTool.off("drawstart", handleDrawStart);
      drawTool.off("drawend", handleDrawEnd);
      drawTool.off("drawcancel", handleDrawCancel);
      map.off("moveend", handleMoveEnd);
      map.off("zoomend", handleZoomEnd);
      drawTool.disable();
      drawTool.remove();
      drawToolRef.current = null;
      selectionLayer.remove();
      selectionLayerRef.current = null;
      clearSelection();
      restoreMapDragging();
      map.remove();
      mapRef.current = null;
      setDrawActive(false);
    };
  }, [attachGeometry, clearSelection, initialBBox, onSelect, open, openStreetMapTileUrl, restoreMapDragging, syncMapLayout]);

  useEffect(() => {
    if (!open || !mapRef.current) {
      return;
    }

    const cleanupLayoutSync = syncMapLayout();

    clearSelection();
    if (!initialBBox) {
      hasRestoredInitialViewRef.current = true;
      return cleanupLayoutSync;
    }

    const geometry = createBBoxPolygon(initialBBox);
    attachGeometry(geometry);

    if (!hasRestoredInitialViewRef.current) {
      const storedMapView = readStoredAreaFilterMapView();
      if (!storedMapView) {
        fitMapToBBox(mapRef.current, initialBBox);
      }
      hasRestoredInitialViewRef.current = true;
    }

    return cleanupLayoutSync;
  }, [attachGeometry, clearSelection, initialBBox, open, syncMapLayout]);

  useEffect(() => {
    if (!open) {
      hasRestoredInitialViewRef.current = false;
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" keepMounted>
      <DialogTitle>Filter annotations by area</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {mapError ? <Alert severity="error">{mapError}</Alert> : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Pan or zoom the map, then use the rectangle tool to pick the area to filter by.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<CropSquareIcon />}
                onClick={handleDrawRectangle}
                disabled={Boolean(mapError) || drawActive}
              >
                Rectangle tool
              </Button>
              <Button variant="text" onClick={clearSelection} disabled={Boolean(mapError) || drawActive}>
                Clear
              </Button>
            </Stack>
          </Stack>
          <Box
            ref={containerRef}
            sx={{
              width: "100%",
              height: { xs: 320, md: 420 },
              borderRadius: 1,
              overflow: "hidden",
              border: (theme) => `1px solid ${theme.palette.divider}`
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnotationAreaFilterDialog;
